import { Anchor, CardSection, Text } from '@zillow/constellation';
import React, { useCallback } from 'react';
import { IBootstrapTableColumnProps, ITableChangeState } from 'react-bootstrap-table-next';
import { IOrderParams } from '../../../../API/pcDashboard/getOrderAsync';
import { IPageData } from '../../../../types/rest/IPageData';
import { ISortedBy } from '../../../../ui-toolkit';
import { clientUrls } from '../../../../ui-toolkit/rest/ClientUrlFactory';
import { convertTableSortToQuerySortBy, useDeterminePagination } from '../../util/tableUtilityMethods';
import { FromToTotalConstellation } from '../signingAppointments/FromToTotal';
import { prettyInternalOrderStatus } from '../../../../ui-toolkit/enums/eInternalOrderStatus';
import { addressDataToString } from '../../../../ui-toolkit/orders/interfaces/IAddressResponse';
import { ISearchOrderData } from '../../../../ui-toolkit/orders/searchOrder/types/ISearchOrder';
import { eSearchOrdersOrderBy } from '../../../../ui-toolkit/orders/searchOrder/types/eSearchOrdersOrderBy';
import { DateFormat, getDateString } from '../../../../ui-toolkit/utilities/getDateString';
import { useHasFeatureFlag } from '../../../../selectors/useHasFeatureFlag';
import { getSpruceRowClasses, SpruceOrderId, StyledSpruceDataGrid } from '../../util/spruceOrderIndicator';
import { useGetConfigQuery } from '../../../../API/applicationConfig/ApplicationConfigAPI';

const createColumns = (
  isZillowClosingEnabled: boolean,
  spruceInstanceHostname?: string
): IBootstrapTableColumnProps<ISearchOrderData>[] => [
  {
    dataField: 'fileNumber',
    text: 'Order ID',
    formatter: (_, row) => {
      const useSpruceLinkFromConfig = isZillowClosingEnabled && (row.isSpruce ?? false);
      const href =
        useSpruceLinkFromConfig && spruceInstanceHostname
          ? clientUrls.urlFor.spruceOrderStatus(row.id, spruceInstanceHostname)
          : clientUrls.urlFor.orderStatus(row.id);

      return (
        <Anchor href={href} target={'_blank'} rel={'noreferrer noopener'}>
          <SpruceOrderId fileNumber={row.fileNumber} showIcon={useSpruceLinkFromConfig} />
        </Anchor>
      );
    },
    sort: true,
  },
  {
    dataField: 'propertyAddress',
    text: 'Address',
    formatter: (_, row) => <Text>{addressDataToString(row.propertyAddress)}</Text>,
  },
  {
    dataField: 'orderPhase',
    text: 'Status',
    formatter: (_, row) => <Text>{prettyInternalOrderStatus[row.orderPhase]}</Text>,
    sort: true,
  },
  {
    dataField: 'closesOn',
    text: 'Closing Date',
    formatter: (_, row) => <Text>{getDateString(DateFormat.short, row.closesOn)}</Text>,
    sort: true,
  },
];

const defaultSorted: ISortedBy[] = [{ dataField: 'createdAt', order: 'desc' }];

export const MultiSelectOrdersTable: React.FC<{
  setQueryObject: (queryObject: IOrderParams) => void;
  data: IPageData<ISearchOrderData>;
  originalArgs?: IOrderParams;
  isFetching: boolean;
  isSuccess: boolean;
}> = ({ setQueryObject, isSuccess, originalArgs, data, isFetching }) => {
  const isZillowClosingEnabled = useHasFeatureFlag('pinecone.zillow-closing');
  const { data: config } = useGetConfigQuery();
  const pagination = useDeterminePagination(data);
  const columns = createColumns(isZillowClosingEnabled, config?.spruceInstanceHostname);

  const onTableChange = useCallback(
    (type: string, tableState: ITableChangeState<ISearchOrderData>) => {
      if (isFetching || !isSuccess) {
        // wait until the data exists before we try setting the QueryString
        return;
      }
      const sortBy = convertTableSortToQuerySortBy(tableState.sortField, tableState.sortOrder) as eSearchOrdersOrderBy;
      setQueryObject({
        ...originalArgs,
        pageNumber: tableState.page - 1,
        pageSize: tableState.sizePerPage,
        sortBy,
      });
    },
    [setQueryObject, isFetching, isSuccess, originalArgs]
  );

  return (
    <CardSection>
      <StyledSpruceDataGrid
        columns={columns}
        keyField={'id'}
        data={data.content}
        remote
        loading={isFetching}
        pagination={pagination}
        onTableChange={onTableChange}
        defaultSorted={defaultSorted}
        zIndex="10"
        rowClasses={(row: ISearchOrderData, rowIndex: number) =>
          getSpruceRowClasses({ ...row, isSpruce: row.isSpruce ?? false }, rowIndex, isZillowClosingEnabled)
        }
      />
      <FromToTotalConstellation
        pageSize={data.pageSize}
        pageNumber={data.pageNumber}
        totalElements={data.totalElements}
      />
    </CardSection>
  );
};
